import { chartMoney, type ProjectedChartPoint } from "./projectedPipeline";

export function ProjectedLandingChart({
  points,
  grainLabel,
}: {
  points: ProjectedChartPoint[];
  grainLabel: string;
}) {
  if (points.length === 0) return null;

  const plotTop = 28;
  const plotBottom = 88;
  const plotH = plotBottom - plotTop;
  const max = Math.max(...points.map((point) => Math.max(point.projected, point.target)), 1);
  const slot = 228 / Math.max(points.length, 1);
  const barW = slot * 0.55;
  const hasTarget = points.some((point) => point.target > 0);

  return (
    <div className="projected-chart-wrap" data-testid="projected-landing-chart">
      <div className="projected-chart-header">
        <h2 className="crm-section-title">Projected vs target</h2>
        <p className="crm-help projected-chart-blurb">
          Bars are open-pipeline value by expected close ({grainLabel}). The tick on each bar is that period&apos;s sales
          target — above the tick means the landing covers the target if those deals win.
        </p>
      </div>
      <svg
        viewBox="0 0 240 124"
        className="projected-chart"
        role="img"
        aria-label={`Projected landing versus target by ${grainLabel}`}
      >
        {points.map((point, index) => {
          const projectedH = point.projected > 0 ? Math.max((point.projected / max) * plotH, 2) : 0;
          const targetY = plotBottom - (point.target / max) * plotH;
          const x = 12 + index * slot + (slot - barW) / 2;
          const onPace = point.target > 0 && point.projected >= point.target;
          return (
            <g key={point.key}>
              <title>
                {`${point.label}: projected ${chartMoney(point.projected)} (${point.count}) / target ${chartMoney(point.target)}`}
              </title>
              {projectedH > 0 && (
                <rect
                  x={x}
                  y={plotBottom - projectedH}
                  width={barW}
                  height={projectedH}
                  rx="1.5"
                  className={onPace ? "projected-chart-bar is-on-pace" : "projected-chart-bar"}
                />
              )}
              {point.target > 0 && (
                <line
                  x1={x - 2}
                  y1={targetY}
                  x2={x + barW + 2}
                  y2={targetY}
                  className="projected-chart-target-mark"
                />
              )}
              {point.projected > 0 && (
                <text
                  x={x + barW / 2}
                  y={plotBottom - projectedH - 4}
                  textAnchor="middle"
                  className="projected-chart-amount"
                  fontSize="13"
                >
                  {chartMoney(point.projected)}
                </text>
              )}
              <text x={x + barW / 2} y="102" textAnchor="middle" className="projected-chart-label" fontSize="6">
                {point.label.replace(/ 20/, " '")}
              </text>
              {point.target > 0 && (
                <text
                  x={x + barW / 2}
                  y="112"
                  textAnchor="middle"
                  className="projected-chart-target-amount"
                  fontSize="6"
                >
                  {chartMoney(point.target)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <ul className="projected-chart-legend">
        <li>
          <span className="projected-chart-swatch projected-chart-swatch--bar" />
          Projected landing
        </li>
        {hasTarget ? (
          <li>
            <span className="projected-chart-swatch projected-chart-swatch--target" />
            Period target
          </li>
        ) : (
          <li>Set salesperson targets under Forecast & KPI to show period markers</li>
        )}
      </ul>
    </div>
  );
}
